import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import CTextField from "../../components/CTextField";
import CSelect from "../../components/CSelect";
import CDataGrid from "../../components/CDataGrid";
import CButton from "../../components/CButton";
import CTabs, { CTabPanel } from "../../components/CTabs";
import SearchIcon from "@mui/icons-material/Search";
import { ReactComponent as FlagIcon } from "../../assets/images/flag-icon.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit-icon.svg";
import { ReactComponent as CheckIcon } from "../../assets/images/check-icon.svg";
import { ReactComponent as UploadIcon } from "../../assets/images/upload-icon.svg";
import { ReactComponent as ToggleBarIcon } from "../../assets/images/toggle-arrow.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { fetchOrganogram } from "../../slices/organogramSlice";
import {
  buildDepartmentHierarchy,
  exportExcel,
  getLocalData,
} from "../../constant/utils/commonUtils";
import Stepper from "../../components/Stepper";
import moment from "moment";
import { fetchDepartmentHierarchyData, fetchReviewData } from "../../slices/trainingFormReviewSlice";
import { fetchAllSelectedTrainings, fetchEmployeesWithoutForm, bulkUpdateFormStatus } from "../../slices/trainingFormSlice";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import { fetchYearId } from "../../slices/yearSlice";
import { Tooltip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { roleType, trainingFormStatus, trainingFormStatusLabels, BULK_FORM_STATUS_OPTIONS } from "../../constant/constants";
import CommentDialog from "../../components/CommentDialog";
import CDialog from "../../components/CDialog";
import { toast } from "react-toastify";
import { isCompanyPrivileged } from "../../utils/roleUtils";
function getSetaDisplayFields(setaTemplate) {
  if (!Array.isArray(setaTemplate)) return { empId: null, firstName: null, lastName: null };
  const fields = {};
  setaTemplate.forEach((item) => {
    const c = item?.config;
    if (!c) return;
    if (c.isEmpId) fields.empId = item.fieldName;
    if (c.isFirstNameField) fields.firstName = item.fieldName;
    if (c.isLastNameField) fields.lastName = item.fieldName;
  });
  return fields;
}

const ReviewTrainingNeeds = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("Employee Submitted Forms");
  const [localData, setLocalData] = useState(null);
  // State for stepper
  const [openStep, setOpenStep] = useState([true, false, false, false]);
  const dispatch = useDispatch();
  const [allSubDepts, setAllSubDepts] = useState([]);
  const [trainingsTrend, setTrainingsTrend] = useState([]);
  const [isDepartmentOpen, setIsDepartmentOpen] = useState(true);
  const [isViewAllSelected, setIsViewAllSelected] = useState(true);
  const initialViewAllFetchDone = useRef(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [employeesWithoutForm, setEmployeesWithoutForm] = useState([]);
  const [loadingAddList, setLoadingAddList] = useState(false);
  const [addDialogSearchQuery, setAddDialogSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDialog, setBulkDialog] = useState({ open: false, targetStatus: null });
  const [bulkComment, setBulkComment] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkStatusSelection, setBulkStatusSelection] = useState("");

  const { data, reviewFormData, selectedDepartment, setaTemplate } = useSelector(
    (state) => ({
      data: state.organogram.data,
      reviewFormData: state.trainingFormReview.reviewFormData,
      selectedDepartment: state.trainingFormReview.selectedDepartment,
      setaTemplate: state.setaTemplate.setaTemplate || [],
    }),
    shallowEqual
  );

  const isPrivilegedUser = isCompanyPrivileged(getLocalData("sdfUser")?.role);

  const isSelectableRow = useCallback(
    (row) =>
      Boolean(row?.id) &&
      row.status &&
      row.status !== trainingFormStatus.DRAFT,
    []
  );

  const selectedRowData = useMemo(
    () => reviewFormData.filter((r) => selectedRows.includes(r.id)),
    [reviewFormData, selectedRows]
  );

  const eligibleBulkRows = useMemo(
    () =>
      bulkDialog.targetStatus
        ? selectedRowData.filter((r) => r.status !== bulkDialog.targetStatus)
        : [],
    [selectedRowData, bulkDialog.targetStatus]
  );

  const refetchReviewData = useCallback(() => {
    const sdfUserData = getLocalData("sdfUser");
    const isSdf = isCompanyPrivileged(sdfUserData?.role);
    if (isViewAllSelected || selectedDepartment === null) {
      if (isSdf) {
        return dispatch(fetchReviewData({ departmentId: null, viewAll: true }));
      }
      if (allSubDepts.length > 0 && allSubDepts[0].departmentId) {
        return dispatch(
          fetchReviewData({
            departmentId: allSubDepts[0].departmentId,
            viewAll: true,
          })
        );
      }
    } else if (selectedDepartment) {
      return dispatch(fetchReviewData({ departmentId: selectedDepartment }));
    }
    return Promise.resolve();
  }, [dispatch, isViewAllSelected, selectedDepartment, allSubDepts]);

  useEffect(() => {
    setSelectedRows([]);
  }, [reviewFormData, selectedDepartment, isViewAllSelected]);

  const setaDisplayFields = useMemo(() => getSetaDisplayFields(setaTemplate), [setaTemplate]);

  useEffect(() => {
    let companyId = null;
    let empId = null;
    if (!data.length) {
      if (!localData) {
        const data = getLocalData("sdfUser");
        companyId = data?.company_id;
        empId = data?.emp_id;
        setLocalData(data);
      }
      dispatch(fetchOrganogram(companyId));
    }
  }, []);

  useEffect(() => {
    if (data.length) {
      const localData = getLocalData("sdfUser");
      let companyId = localData?.company_id;
      let empId = localData?.emp_id;
      dispatch(
        fetchDepartmentHierarchyData({ companyId: companyId, empId })
      ).then((res) => {
        if (!res.error) {
          let allSubDepts = buildDepartmentHierarchy(res.payload, data);
          const user = getLocalData("sdfUser");
          const isSdf = isCompanyPrivileged(user?.role);
          // "None" (no org department): only SDF may filter here; managers use real departments only
          if (isSdf) {
            const noneNode = {
              id: "none",
              name: "None",
              departmentId: "none",
              sub_departments: [],
            };
            allSubDepts = Array.isArray(allSubDepts)
              ? [...allSubDepts, noneNode]
              : [noneNode];
          }
          setAllSubDepts(allSubDepts);
        }
      });
    }
  }, [data]);

  // // Non-SDF users must not keep "None" selected (hidden for them; e.g. persisted Redux or stale session)
  useEffect(() => {
    const user = getLocalData("sdfUser");
    if (isCompanyPrivileged(user?.role)) return;
    if (selectedDepartment !== "none") return;
    if (!allSubDepts.length) return;
    const firstReal = allSubDepts.find(
      (d) => String(d?.departmentId ?? "").toLowerCase() !== "none",
    );
    if (firstReal?.departmentId != null) {
      dispatch(fetchReviewData({ departmentId: firstReal.departmentId }));
    }
  }, [selectedDepartment, allSubDepts, dispatch]);

  useEffect(() => {
    if(!reviewFormData.length){ setTrainingsTrend([]); return;}
    const trainingsTrend = new Map();
    reviewFormData.forEach(ele => {
      const { "Training Name": trainingName } = ele;
      (trainingName ?? []).forEach(ele => {
        if(trainingsTrend.has(ele)){
          trainingsTrend.set(ele, trainingsTrend.get(ele) + 1);
        }else{
          trainingsTrend.set(ele, 1);
        }
      });
    });
    // This code creates a sorted array of training names and their counts, in descending order of frequency.
    const sortedTrainingsTrend = Array.from(trainingsTrend.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, selection_count],index) => ({ name, selection_count, id: index }));
    setTrainingsTrend(sortedTrainingsTrend || []);
  }, [reviewFormData]);

  // Clear View All selection when a department is selected
  useEffect(() => {
    if (selectedDepartment !== null) {
      setIsViewAllSelected(false);
    }
  }, [selectedDepartment]);

  // Handler for step click
  const handleStepClick = (level) => {
    setOpenStep((prev) => prev.map((v, i) => (i <= level ? true : false)));
  };

  const handleViewAllDepartments = () => {
    const sdfUserData = getLocalData("sdfUser");
    const isSdfUser = isCompanyPrivileged(sdfUserData?.role);

    setIsViewAllSelected(true);

    if (isSdfUser) {
      dispatch(
        fetchReviewData({
          departmentId: null,
          viewAll: true,
        })
      );
    } else if (allSubDepts.length > 0 && allSubDepts[0].departmentId) {
      dispatch(
        fetchReviewData({
          departmentId: allSubDepts[0].departmentId,
          viewAll: true,
        })
      );
    }
  };

  // Default to "All Departments" (same as handleViewAllDepartments) instead of first tree node
  useEffect(() => {
    if (initialViewAllFetchDone.current) return;
    const user = getLocalData("sdfUser");
    const isSdfUser = isCompanyPrivileged(user?.role);
    if (isSdfUser) {
      initialViewAllFetchDone.current = true;
      dispatch(
        fetchReviewData({
          departmentId: null,
          viewAll: true,
        })
      );
      return;
    }
    if (!allSubDepts.length || !allSubDepts[0]?.departmentId) return;
    initialViewAllFetchDone.current = true;
    dispatch(
      fetchReviewData({
        departmentId: allSubDepts[0].departmentId,
        viewAll: true,
      })
    );
  }, [allSubDepts, dispatch]);

  // useEffect(() => {
  //   if (activeTab === "Selected Trainings") {
  //     dispatch(fetchYearId()).then((res) => {
  //       let yearId = res.payload;
  //       dispatch(
  //         fetchAllSelectedTrainings({
  //           company_id: getLocalData().company_id,
  //           year_id: yearId,
  //         })
  //       ).then((res) => {
  //         if (!res.error) {
  //           setTrainingsTrend(res.payload);
  //         }
  //       });
  //     });
  //   }
  // }, [activeTab]);

  const tabs = [
    { label: "Employee Submitted Forms", value: "Employee Submitted Forms" },
    { label: "Selected Training", value: "Selected Training" },
  ];

  
  const columns = [
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      renderCell: ({ value }) => (
        <div className={`flex flex-col status-badge ${ 
          value === trainingFormStatus.DRAFT
          ? "status-saved"
          : value === trainingFormStatus.REQUESTED
          ? "status-request-submit"
          : value === trainingFormStatus.RECOMMENDED
          ? "status-recommend"
          : value === trainingFormStatus.APPROVED
          ? "status-approved"
          : value === trainingFormStatus.REQUEST_EDIT
          ? "status-request-edit"
          :""
      } `}>{trainingFormStatusLabels[value] || value || "-"}</div>
      ),
    },
    {
      field: "Employee Id",
      headerName: "Personnel No.",
      minWidth: 120,
      renderCell: (params) => {
        const search = params.row.employment_type
          ? `?employment_type=${encodeURIComponent(params.row.employment_type)}`
          : "";
        return (
          <div
            className="cursor-pointer hover:text-blue-600"
            onClick={() => navigate(`/form/${params.row.id}${search}`)}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: "Employee Name",
      headerName: "Employee Name",
      minWidth: 150,
      renderCell: (params) => {
        const search = params.row.employment_type
          ? `?employment_type=${encodeURIComponent(params.row.employment_type)}`
          : "";
        return (
          <div
            className="cursor-pointer hover:text-blue-600"
            onClick={() => navigate(`/form/${params.row.id}${search}`)}
          >
            {params.value}
          </div>
        );
      },
    },
    { field: "job", headerName: "Job", minWidth: 120 },
    {
      field: "skills",
      headerName: "Skills",
      minWidth: 200,
      renderCell: ({ value }) => {
        const colors = {
          high: "#FF6464",
          medium: "#FECE66",
          low: "#F2DDAF",
        };
        return (
          <div className="flex flex-col">
            {value || value?.length ? (
              value.map((skill, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Tooltip
                    title={
                      skill.priorityLevel.charAt(0).toUpperCase() +
                      skill.priorityLevel.slice(1)
                    }
                    arrow
                    placement={"top"}
                    className="!text-xs !leading-xs text-center"
                  >
                    <span
                      className={`pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[${
                        colors[skill.priorityLevel.toLowerCase()]
                      }] before:rounded-sm before:z-[1]`}
                    ></span>
                  </Tooltip>
                  <span className="capitalize">{skill.name}</span>
                </div>
              ))
            ) : (
              <div className="px-2 rounded inline-block">-</div>
            )}
          </div>
        );
      },
    },
    {
      field: "Skills Requirement Motivation",
      headerName: "Skill Requirement motivation",
      minWidth: 250,
      renderCell: ({ value }) => {
        return (
          <div className="flex flex-col">
            {value?.map((motivation, idx) => (
              <div
                key={idx}
                className="px-2 rounded inline-block truncate max-w-[230px]"
                title={motivation || "-"}
              >
                {motivation || "-"}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: "Training Name",
      headerName: "Training Name",
      minWidth: 180,
      renderCell: ({ value }) => {
        return (
          <div className="flex flex-col">
            {value.map((trainingName, idx) => (
              <div key={idx} className="px-2 rounded inline-block">
                {trainingName}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: "Training Type",
      headerName: "Training Type",
      minWidth: 250,
      renderCell: ({ value }) => (
        <div className="flex flex-col">
          {value || value?.length ? (
            value.map((trainingType, idx) => (
              <div key={idx} className="px-2 rounded inline-block">
                {trainingType}
              </div>
            ))
          ) : (
            <div className="px-2 rounded inline-block">-</div>
          )}
        </div>
      ),
    },
    { field: "Manager Name", headerName: "Manager Name", minWidth: 180 },
    { field: "Manager Email", headerName: "Manager Email", minWidth: 180 },
    {
      field: "Desired Date",
      headerName: "Desired Date of completion",
      minWidth: 180,
      renderCell: ({ value }) => (
        <div className="flex flex-col">
          {value || value?.length ? (
            value.map((desiredDate, idx) => (
              <div key={idx} className="px-2 rounded inline-block">
                {desiredDate ? moment(desiredDate).format("DD/MM/YYYY") : "-"}
              </div>
            ))
          ) : (
            <div className="px-2 rounded inline-block">-</div>
          )}
        </div>
      ),
    },
    // {
    //   field: "Training Date",
    //   headerName: "Desired Date of completion",
    //   minWidth: 180,
    //   renderCell: ({ value }) => (
    //     <div className="flex flex-col">
    //       {value || value?.length ? (
    //         value.map((trainingDate, idx) => (
    //           <div key={idx} className="px-2 rounded inline-block">
    //             {trainingDate}
    //           </div>
    //         ))
    //       ) : (
    //         <div className="px-2 rounded inline-block">-</div>
    //       )}
    //     </div>
    //   ),
    // },

    {
      field: "actions",
      headerName: "Actions",
      minWidth: 140,
      renderCell: (params) => {
        return (
          <div className="flex 2xl:gap-3 gap-2 items-center h-full">
            {/* Check Button */}
            {/* <CButton
              tooltip="Check"
              tooltipPosition={"top"}
              className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
            >
              <CheckIcon className="!fill-blue-500 !text-blue-500" />
            </CButton> */}
            {/* Flag Button */}
            {/* <CButton
              tooltip="Flag"
              tooltipPosition={"top"}
              className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
            >
              <FlagIcon className="!fill-blue-500 !text-blue-500" />
            </CButton> */}
            {/* Delete Button */}
            <CButton
              tooltip="Edit"
              tooltipPosition={"top"}
              className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
              onClick={() => {
                const search = params.row.employment_type
                  ? `?employment_type=${encodeURIComponent(params.row.employment_type)}`
                  : "";
                navigate(`/form/${params.row.id}${search}`);
              }}
            >
              <EditIcon className="!fill-blue-500 !text-blue-500" />
            </CButton>
          </div>
        );
      },
    },
  ];

  const rows = [
    {
      id: 1,
      employeeId: "101",
      employeeName: "John C",
      job: "Exec.",
      skills: [
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FF6464] before:rounded-sm before:z-[1]">
          Time Management
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FECE66] before:rounded-sm before:z-[1]">
          Java Programming
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#F2DDAF] before:rounded-sm before:z-[1]">
          Communication
        </span>,
      ],
      desiredDate: [
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FF6464] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FECE66] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#F2DDAF] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
      ],
      motivation: [<span>Some Reason</span>, <span>Some Reason</span>],
      trainingName: [<span>ABC Training</span>, <span>XYZ Training</span>],
      trainingDate: [
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FF6464] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FECE66] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#F2DDAF] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
      ],
      trainingType: [
        <span>Short Term</span>,
        <span>Long Term</span>,
        <span>Qualification</span>,
      ],
      managerName: "Mark",
      managerEmail: "AB@gmail.com",
      status: trainingFormStatus.RECOMMENDED,
      actions: "",
    },
    {
      id: 2,
      employeeId: "101",
      employeeName: "John C",
      job: "Exec.",
      skills: [
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FF6464] before:rounded-sm before:z-[1]">
          Time Management
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FECE66] before:rounded-sm before:z-[1]">
          Java Programming
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#F2DDAF] before:rounded-sm before:z-[1]">
          Communication
        </span>,
      ],
      desiredDate: [
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FF6464] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FECE66] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#F2DDAF] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
      ],
      motivation: [<span>Some Reason</span>, <span>Some Reason</span>],
      trainingName: [<span>ABC Training</span>, <span>XYZ Training</span>],
      trainingDate: [
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FF6464] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#FECE66] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
        <span className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[calc(50%-5px)] before:w-[10px] before:h-[10px] before:bg-[#F2DDAF] before:rounded-sm before:z-[1]">
          01/05/2025
        </span>,
      ],
      trainingType: [
        <span>Short Term</span>,
        <span>Long Term</span>,
        <span>Qualification</span>,
      ],
      managerName: "Mark",
      managerEmail: "AB@gmail.com",
      status: trainingFormStatus.RECOMMENDED,
      actions: "",
    },
  ];

  const trainingColumns = [
    {
      field: "name",
      headerName: "Training Name",
      flex: 1,
    },
    {
      field: "selection_count",
      headerName: "No. Of Employee Preferred",
      flex: 1,
    },
  ];
  const trainingRows = [
    {
      id: 1,
      name: "",
      selection_count: "",
    },
    {
      id: 2,
      trainingName: "",
      employeePreferred: "",
    },
    {
      id: 3,
      trainingName: "",
      employeePreferred: "",
    },
    {
      id: 4,
      trainingName: "",
      employeePreferred: "",
    },
  ];

  const handleAddClick = async () => {
    const user = getLocalData("sdfUser");
    if (!user?.company_id) {
      toast.error("Company not found.");
      return;
    }
    setAddDialogOpen(true);
    setLoadingAddList(true);
    setEmployeesWithoutForm([]);
    try {
      if (!setaTemplate.length) {
        dispatch(fetchSetaTemplateByCompanyId(user.company_id));
      }
      const yearRes = await dispatch(fetchYearId());
      const yearId = yearRes.payload;
      if (!yearId) {
        toast.error("Could not load current year.");
        setLoadingAddList(false);
        return;
      }
      const res = await dispatch(fetchEmployeesWithoutForm({ companyId: user.company_id, yearId }));
      if (res.meta?.requestStatus === "fulfilled" && Array.isArray(res.payload)) {
        const d = setaDisplayFields;
        const rows = (res.payload ?? []).map((row) => {
          const data = row.data ?? {};
          const personnelNo =
            row.emp_id ??
            (d.empId ? data[d.empId] ?? data?.data?.[d.empId] : null) ??
            "-";
          const first =
            row.first_name ??
            (d.firstName ? data[d.firstName] ?? data?.data?.[d.firstName] : null) ??
            "";
          const last =
            row.last_name ??
            (d.lastName ? data[d.lastName] ?? data?.data?.[d.lastName] : null) ??
            "";
          const name = `${first} ${last}`.trim() || "-";
          return { ...row, personnel_no_display: personnelNo, employee_name_display: name };
        });
        setEmployeesWithoutForm(rows);
      } else {
        toast.error(res.payload || "Failed to load employees.");
      }
    } catch (e) {
      toast.error(e?.message || "Failed to load employees.");
    } finally {
      setLoadingAddList(false);
    }
  };

  // Same as employment_type: direct row fields from RPC (normalized on load so name/personnel show like employment_type)
  const addListColumns = [
    { field: "personnel_no_display", headerName: "Personnel No.", minWidth: 120, flex: 0.5 },
    { field: "employee_name_display", headerName: "Employee Name", minWidth: 180, flex: 1 },
    { field: "employment_type", headerName: "Employment Type", minWidth: 140, flex: 0.6 },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 120,
      flex: 0.4,
      sortable: false,
      renderCell: (params) => (
        <CButton
          onClick={(e) => {
            e.stopPropagation();
            handleSelectEmployee(params.row);
          }}
          className="!h-8 !min-h-[32px] !text-xs !px-3 bg-blue-500 hover:bg-blue-600 text-white"
        >
          Fill Form
        </CButton>
      ),
    },
  ];

  const handleSelectEmployee = (row) => {
    setAddDialogOpen(false);
    const params = new URLSearchParams({ emp_id: row.id, sdf_on_behalf: "1" });
    if (row.employment_type) params.set("employment_type", row.employment_type);
    navigate(`/form?${params.toString()}`);
  };

  const openBulkStatusChange = (targetStatus) => {
    if (!selectedRows.length) {
      toast.warning("Select at least one form");
      return;
    }
    if (!targetStatus) {
      return;
    }
    const eligible = selectedRowData.filter((r) => r.status !== targetStatus);
    if (!eligible.length) {
      toast.warning(
        `All selected forms are already "${trainingFormStatusLabels[targetStatus] || targetStatus}"`
      );
      setBulkStatusSelection("");
      return;
    }
    setBulkComment("");
    setBulkDialog({ open: true, targetStatus });
  };

  const handleBulkStatusSelect = (event) => {
    const targetStatus = event.target.value;
    setBulkStatusSelection(targetStatus);
    if (targetStatus) {
      openBulkStatusChange(targetStatus);
      setBulkStatusSelection("");
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkComment?.trim()) {
      toast.error("Please provide a comment for this action.");
      return;
    }

    const targetStatus = bulkDialog.targetStatus;
    if (!targetStatus) {
      return;
    }

    const items = eligibleBulkRows.map((r) => ({
      formId: r.id,
      targetStatus,
    }));

    if (!items.length) {
      toast.warning("No forms to update");
      return;
    }

    setBulkSubmitting(true);
    try {
      const res = await dispatch(
        bulkUpdateFormStatus({ items, comment: bulkComment.trim() })
      );
      if (bulkUpdateFormStatus.rejected.match(res)) {
        toast.error(res.payload || "Failed to update form status");
        return;
      }
      const { succeeded } = res.payload;
      const skipped = selectedRowData.length - items.length;
      const statusLabel =
        trainingFormStatusLabels[targetStatus] || targetStatus;
      toast.success(
        `${succeeded} form(s) changed to ${statusLabel}` +
          (skipped > 0 ? ` (${skipped} already at this status)` : "")
      );
      setBulkDialog({ open: false, targetStatus: null });
      setBulkComment("");
      setBulkStatusSelection("");
      setSelectedRows([]);
      await refetchReviewData();
    } finally {
      setBulkSubmitting(false);
    }
  };

const handleExport = () => {
  const fieldToHeaderMap = {};
  const exportableFields = columns
    .filter((col) => col.field !== "actions")
    .map((col) => {
      fieldToHeaderMap[col.field] = col.headerName;
      return col.field;
    });

  const dataWithoutId = reviewFormData.map((record) => {
    const processed = {};

    exportableFields.forEach((field) => {
      let value = record[field];

      if (Array.isArray(value)) {
      if (field === "skills" && value.every(item => typeof item === 'object' && item !== null)) {
        value = value.map((skill) => `${skill.name} (${skill.priorityLevel})`).join(", ");
      }
      else if (field === "Desired Date" && value.every(item => typeof item === 'string')) {
        const formatDate = (dateStr) => {
          const m = moment.parseZone(dateStr);
          return m.isValid() ? m.format("DD/MM/YYYY") : dateStr;
        };
        value = value.map(formatDate).join(", ");
      }
      else if (value.every(item => typeof item === 'string')) {
        value = value.join(", ");
      }
      
    }
    // Handle a single date string  
    else if (field === "Desired Date" && typeof value === 'string') {
        const formatDate = (dateStr) => {
          const m = moment.parseZone(dateStr);
          return m.isValid() ? m.format("DD/MM/YYYY") : dateStr;
        };
      value = formatDate(value);
    }
      processed[fieldToHeaderMap[field]] = value ?? "-";
  });

    return processed;
  });



  exportExcel(dataWithoutId, "Training survey forms");
};

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="!font-[Poppins] xl:text-3xl xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold">
            Manage Training Needs
          </h2>
          <p className="!font-[Poppins] lg:text-sm lg:leading-sm text-xs leading-xs font-medium text-grey-700 mt-1">
            {/* Review and analyze training needs submitted by employees across different departments. */}
          </p>
        </div>
        {isCompanyPrivileged(getLocalData("sdfUser")?.role) && (
          <CButton
            className="bg-blue-500 hover:bg-blue-600 text-white 2xl:text-lg text-base !h-12 !px-6"
            onClick={handleAddClick}
          >
            Allocate Training
          </CButton>
        )}
      </div>

      <div className={`flex flex-wrap justify-between mt-4 min-h-[calc(100vh-164px)] ${isDepartmentOpen ? "gap-2" : "gap-0"}`}>
        <div className={` bg-white border border-solid border-blue-200 rounded-xl overflow-hidden overflow-x-auto ${isDepartmentOpen ? "w-[calc(25%-4px)]  opacity-100  p-4" : "w-0 opacity-0  p-0 border-none h-0"}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="!font-[Poppins] text-lg font-semibold text-blue-500 opacity-70">
              Departments
            </h3>
            
            <CButton onClick={() => setIsDepartmentOpen(!isDepartmentOpen)} className="!p-0">
              <ToggleBarIcon className={`w-[24px] h-[24px] !stroke-blue-500 ${isDepartmentOpen ? "" : "hidden"}`} />
            </CButton>
          </div>
          <ul className="list-none p-0 m-0">
            {/* <li
              className="flex items-center font-semibold 2xl:text-xl 2xl:leading-xl text-base leading-base text-gray-700 mb-3 cursor-pointer"
              onClick={() => handleStepClick(0)}
            >
              <span
                className={
                  "text-[#1A7EB3] mr-2 transition-transform duration-200" +
                  (openStep[0] ? " rotate-90" : "")
                }
              >
                <RightArrowIcon className="w-4 h-4" />
              </span>{" "}
              Business unit
            </li>
            {openStep[0] && (
              <li
                className="ml-6 font-medium flex items-center  text-gray-300 2xl:text-base 2xl:leading-base text-sm leading-sm mb-3 cursor-pointer"
                onClick={() => handleStepClick(1)}
              >
                <span
                  className={
                    "mr-2 transition-transform duration-200" +
                    (openStep[1] ? " rotate-90" : "")
                  }
                >
                  <RightArrowIcon className="w-4 h-4" />
                </span>{" "}
                Head Department
              </li>
            )}
            {openStep[1] && (
              <li
                className="ml-12 font-medium flex items-center  text-gray-300 2xl:text-base 2xl:leading-base text-sm leading-sm mb-3 cursor-pointer"
                onClick={() => handleStepClick(2)}
              >
                <span
                  className={
                    "mr-2 transition-transform duration-200" +
                    (openStep[2] ? " rotate-90" : "")
                  }
                >
                  <RightArrowIcon className="w-4 h-4" />
                </span>{" "}
                Sub Department
              </li>
            )}
            {openStep[2] && (
              <li
                className="ml-20 font-medium flex items-center  text-gray-300 2xl:text-base 2xl:leading-base text-sm leading-sm mb-3 cursor-pointer"
                onClick={() => handleStepClick(3)}
              >
                <span
                  className={
                    "mr-2 transition-transform duration-200" +
                    (openStep[3] ? " rotate-90" : "")
                  }
                >
                  <RightArrowIcon className="w-4 h-4" />
                </span>{" "}
                Sub Sub Department
              </li>
            )}
            {openStep[3] && (
              <li className="ml-32 font-medium text-gray-300 2xl:text-base 2xl:leading-base text-sm leading-sm mb-3">
                Sub Sub Sub Department
              </li>
            )} */}
             <CButton
              className={`!min-w-0 !px-0 !py-0 text-xs bg-transparent font-semibold hover:bg-transparent rounded-md ml-5 !h-auto ${
                isViewAllSelected ? "text-blue-500" : "text-gray-700"
              }`}
              onClick={handleViewAllDepartments}
            >
               All Departments
            </CButton> 
            <Stepper data={allSubDepts} />
          </ul>
        </div>
        <div className={`p-4 w-[calc(75%-4px)] bg-white border border-solid border-blue-200 rounded-xl overflow-hidden ${isDepartmentOpen ? "w-[calc(75%-4px)]" : "w-full"}`}>
          {/* Tabs */}
          <div className="flex items-center gap-2 mb-4">
            <CButton onClick={() => setIsDepartmentOpen(!isDepartmentOpen)} className={`${isDepartmentOpen ? "hidden" : "rotate-180"}`}>
              <ToggleBarIcon className={`w-[24px] h-[24px] !stroke-blue-500 `} />
            </CButton>
            <CTabs
              tabs={tabs}
              value={activeTab}
              onChange={setActiveTab}
            />
          </div>
          <CTabPanel value={activeTab} index="Employee Submitted Forms">
            {/* Table Section */}
            <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden ">
              <div className="flex items-center justify-between p-3">
                <CTextField
                  name="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  startIcon={<SearchIcon className="text-blue-300" />}
                  placeholder={"Search"}
                  className="!mt-0 w-full 2xl:max-w-[400px] max-w-[300px]"
                />
                <div className="flex items-center gap-2">
                  {isPrivilegedUser && (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-large text-gray-700 whitespace-nowrap">
                        Change to
                      </span>
                      <CSelect
                        name="bulkStatusChange"
                        value={bulkStatusSelection}
                        onChange={handleBulkStatusSelect}
                        options={BULK_FORM_STATUS_OPTIONS}
                        placeholder="Status change to..."
                        disabled={!selectedRows.length || bulkSubmitting}
                        formControlClass="!min-w-[220px] !w-auto"
                        className="!h-10"
                      />
                    </div>
                  )}
                  <CButton
                    className=" text-white gradient-btn rounded-lg p-0 ml-3 "
                    onClick={handleExport}
                  >
                    Export All
                  </CButton>
                </div>
              </div>
              {/* Data Table */}
              <div className="w-full  h-[calc(100vh-338px)]">
                <CDataGrid
                  columns={columns}
                  rows={reviewFormData}
                  searchQuery={searchQuery}
                  idKey="id"
                  className="bg-white shadow-md rounded-lg training-table"
                  getRowHeight={() => "auto"}
                  checkboxSelection={isPrivilegedUser}
                  checkboxSelectionVisibleOnly={false}
                  isRowSelectable={(params) => isSelectableRow(params.row)}
                  rowSelectionModel={selectedRows}
                  onRowSelectionModelChange={setSelectedRows}
                  keepNonExistentRowsSelected={false}
                />
              </div>
            </div>
          </CTabPanel>
          <CTabPanel value={activeTab} index="Selected Training">
            {/* Table Section */}
            <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden ">
              <div className="flex items-center justify-between p-3">
                <CTextField
                  name="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  startIcon={<SearchIcon className="text-blue-300" />}
                  placeholder={"Search"}
                  className="!mt-0 w-full 2xl:max-w-[400px] max-w-[300px]"
                />
                {/* <div className="flex items-center">
                  <CButton className=" text-white bg-blue-500 hover:bg-blue-700 rounded-lg p-0 ml-3 ">
                    <UploadIcon className="w-5 h-5 !fill-white" />
                    Upload Training Needs
                  </CButton>
                </div> */}
              </div>
              {/* Data Table */}
              <div className="w-full h-[calc(100vh-366px)]">
                <CDataGrid
                  columns={trainingColumns}
                  rows={trainingsTrend}
                  searchQuery={searchQuery}
                  idKey="id"
                  className="bg-white shadow-md rounded-lg"
                />
              </div>
            </div>
          </CTabPanel>
        </div>
      </div>

      {/* Allocate Training modal — large viewport-style dialog (~90% width/height) */}
      <CDialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setAddDialogSearchQuery("");
        }}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 0,
            width: { xs: "calc(100vw - 24px)", sm: "min(1400px, 90vw)" },
            maxWidth: "none",
            height: { xs: "calc(100vh - 24px)", sm: "90vh" },
            minHeight: { xs: "auto", sm: "70vh" },
            maxHeight: "calc(100vh - 32px)",
            m: 2,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <div className="flex flex-col flex-1 min-h-0 h-full p-5 sm:p-6 md:p-8">
          {/* Header: title left, close X top-right */}
          <div className="flex items-center justify-between relative mb-5 flex-shrink-0">
            <h3 className="text-2xl md:text-3xl font-bold text-blue-500">
              Allocate Training
            </h3>
            <IconButton
              onClick={() => {
                setAddDialogOpen(false);
                setAddDialogSearchQuery("");
              }}
              size="small"
              sx={{ color: "#6b7280", padding: "4px" }}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </div>
          {/* Body: search + table */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {loadingAddList ? (
              <p className="text-gray-600 py-6">Loading employees...</p>
            ) : employeesWithoutForm.length === 0 ? (
              <p className="text-gray-600 py-6">No employees without a form for this year.</p>
            ) : (
              <>
                <div className="mb-4 flex-shrink-0">
                  <CTextField
                    name="addDialogSearch"
                    value={addDialogSearchQuery}
                    onChange={(e) => setAddDialogSearchQuery(e.target.value)}
                    startIcon={<SearchIcon className="text-blue-300" />}
                    placeholder="Search"
                    className="!mt-0 w-full !bg-gray-100 !border !border-gray-200 !rounded"
                  />
                </div>
                <div className="flex-1 min-h-0 [&_.MuiDataGrid-row]:cursor-pointer [&_.MuiDataGrid-row:hover]:bg-blue-50">
                  <CDataGrid
                    rows={employeesWithoutForm}
                    columns={addListColumns}
                    idKey="id"
                    pageSize={10}
                    checkboxSelection={false}
                    onRowClick={({ row }) => handleSelectEmployee(row)}
                    searchQuery={addDialogSearchQuery}
                    height="100%"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </CDialog>

      {bulkDialog.open && (
        <CommentDialog
          open={bulkDialog.open}
          onClose={() => {
            setBulkDialog({ open: false, targetStatus: null });
            setBulkComment("");
            setBulkStatusSelection("");
          }}
          onSubmit={handleBulkSubmit}
          title={
            BULK_FORM_STATUS_OPTIONS.find(
              (o) => o.value === bulkDialog.targetStatus,
            )?.label || "Bulk status change"
          }
          description={`Updating ${eligibleBulkRows.length} of ${selectedRowData.length} selected form(s). A comment is required for audit history.`}
          comment={bulkComment}
          onCommentChange={(e) => setBulkComment(e.target.value)}
          loading={bulkSubmitting}
        />
      )}
    </div>
  );
};

export default ReviewTrainingNeeds;
